import React from 'react'

import { Body2, Box, Color, Text, TextAlign, withCitrusTheme } from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'

import type { RecipeQueryResponse } from '@library/api-menu-experience'
import type { MenuRecipeImage, RecipeImage as LegacyRecipeImage } from '@library/menu-service'

import { getBrowserType } from 'selectors/browser'
import { getFeaturedImage, getRangeImages } from 'utils/image'

import { DetailCarousel } from '../Carousel'
import { DetailImage } from '../DetailImage'

type RecipeImageProps = {
  title: string
  banner?: RecipeQueryResponse['tagGroups']['banner']
  images: {
    partial?: LegacyRecipeImage[]
    full?: MenuRecipeImage[]
    experience?: RecipeQueryResponse['images']
  }
}

type BannerTag = NonNullable<NonNullable<RecipeImageProps['banner']>['tags']>[number]

const ImageContainer = withCitrusTheme(Box, (theme) => ({
  position: 'relative',
  marginLeft: `-${theme.spacing(4)}px`,
  marginTop: `-${theme.spacing(4)}px`,
  width: `calc(100% + 2*${theme.spacing(4)}px)`,
  [`@media (min-width: ${theme.tokens.breakpoints[0]})`]: {
    marginLeft: `-${theme.spacing(6)}px`,
    marginTop: `-${theme.spacing(6)}px`,
    width: `calc(100% + 2*${theme.spacing(6)}px)`,
  },
}))

const Banner = withCitrusTheme(Box, (theme) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  padding: `${theme.spacing(2)}px 0`,
}))

const Separator = () => (
  <Body2 aria-hidden="true" size={2} color={Color.Inherit} style={{ whiteSpace: 'nowrap' }}>
    •
  </Body2>
)

const BannerText = ({ text }: { text: string }) => (
  <Text size={[1, 4]} textAlign={TextAlign.Center} color={Color.Inherit} style={{ flexGrow: 1 }}>
    {text}
  </Text>
)

export const RecipeImage = ({ images, title, banner }: RecipeImageProps) => {
  const { partial: partialImages, full: fullImages, experience: experienceImages } = images
  const browserType = useSelector(getBrowserType)
  const bannerTags =
    banner?.tags?.reduce<Array<BannerTag & { key: number }>>((acc, tag, index) => {
      if (!tag.text) return acc
      acc.push({ ...tag, key: index })

      return acc
    }, []) ?? []
  const hasBanner = bannerTags.length > 0

  const featuredImage =
    experienceImages?.[0]?.crops || getFeaturedImage(partialImages, 'standard', browserType)
  const fallbackFeaturedImage =
    featuredImage.length > 0 ? featuredImage : fullImages?.[0]?.crops || []
  const rangeImages =
    experienceImages && experienceImages.length > 1 ? experienceImages : getRangeImages(fullImages)

  return (
    <ImageContainer>
      {rangeImages.length > 0 ? (
        <DetailCarousel images={rangeImages} />
      ) : (
        <>
          <DetailImage media={fallbackFeaturedImage} title={title} />
          {hasBanner && (
            <Banner
              data-testid="recipe-banner"
              style={{
                backgroundColor: banner?.colors?.backgroundColor?.light,
                color: banner?.colors?.color?.light,
              }}
            >
              {bannerTags.map((tag, index) => (
                <React.Fragment key={tag.key}>
                  {index > 0 && <Separator />}
                  <BannerText text={tag.text} />
                </React.Fragment>
              ))}
            </Banner>
          )}
        </>
      )}
    </ImageContainer>
  )
}
